import axios from 'axios';
import { API } from '../../../config/api';

const http = axios.create({
  baseURL: API.BACKEND,
  timeout: 10000
});

// `axios.create()` snapshots the global defaults, so a token written later by
// `setAuthToken` never reaches this instance. See features/jobs/api/http.js.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// The page's scope bar offers product and operator on every tab, so this has to
// carry both — it used to drop them silently, which is why changing either did
// nothing on Overview while the tab beside it narrowed correctly.
export async function fetchOperatorSummary({
  machineId, phase, productLabel, operator, operators, dateFrom, dateTo
} = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (phase) params.phase = phase;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  else if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/operator-summary', { params });
  return data;
}

export async function fetchOperatorComparison({ machineId, phase, wireType, section, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (phase) params.phase = phase;
  if (wireType) params.wireType = wireType;
  if (section) params.section = section;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/operator-comparison', { params });
  return data;
}

export async function fetchOperatorMaterial({ machineId, operator, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/operator-material', { params });
  return data;
}

export async function fetchProductPerformance({ machineId, operator, phase, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (operator) params.operator = operator;
  if (phase) params.phase = phase;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/product-performance', { params });
  return data;
}

export async function fetchChangeoverSummary({ machineId, operator, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/changeover-events/summary', { params });
  return data;
}

export async function fetchChangeoverByProduct({ machineId, operator, type, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (operator) params.operator = operator;
  if (type) params.type = type;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/changeover-events/by-product', { params });
  return data;
}

// Operator × product × phase, each row carrying the median of everyone else who
// ran the same work. Filtering by operator happens server-side AFTER the peer
// medians are computed, so a filtered view still compares against the group.
export async function fetchOperatorMatrix({ machineId, phase, productLabel, operator, operators, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (phase) params.phase = phase;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  else if (operator) params.operator = operator;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/operator-matrix', { params });
  return data;
}

// Material as kg/km per operator, on the same product and phase.
export async function fetchMaterialMatrix({ machineId, phase, productLabel, operator, operators, material, dateFrom, dateTo } = {}) {
  const params = {};
  if (machineId) params.machineId = machineId;
  if (phase) params.phase = phase;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  else if (operator) params.operator = operator;
  if (material) params.material = material;
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/material-matrix', { params });
  return data;
}

// Speed and downtime per operator per week/day — the half the matrices cannot
// show, since a 90-day median hides whether things are improving.
export async function fetchOperatorTrend({ bucket, machineId, phase, productLabel, operators, dateFrom, dateTo } = {}) {
  const params = {};
  if (bucket) params.bucket = bucket;
  if (machineId) params.machineId = machineId;
  if (phase) params.phase = phase;
  if (productLabel) params.productLabel = productLabel;
  if (Array.isArray(operators) && operators.length) params.operators = operators.join(',');
  if (dateFrom) params.dateFrom = dateFrom;
  if (dateTo) params.dateTo = dateTo;

  const { data } = await http.get('/api/v1/production-metrics/operator-trend', { params });
  return data;
}
